import { defineStore } from 'pinia'
import { logger } from '@/logging/logger'
import { LogCategory } from '@/logging/types'
import { type GuidedModeSnapshot, useGuidedModeStore } from '@/features/guided-mode/stores/guidedMode'
import {
  type OverallAssessmentJourney,
  useOverallAssessmentStore,
} from '@/features/overall-assessment/stores/overallAssessment'
import {
  clearActiveTextModeContext,
  getActiveTextModeContext,
  setActiveTextModeContext,
  type TextModeAnalyticsContext,
} from '@/features/text-mode/analytics/text-mode-context'
import { type AlertStoreState, useAlertStore } from '@/features/text-mode/stores/alert.store'
import { type DraftStoreSnapshot, useDraftStore } from '@/features/text-mode/stores/draft.store'
import { type EvidenceState, useEvidenceStore } from '@/features/text-mode/stores/evidence.store'
import { type NoteState, useNoteStore } from '@/features/text-mode/stores/note.store'
import { type PatientState, usePatientStore } from '@/features/text-mode/stores/patient.store'
import { type QuestionState, useQuestionStore } from '@/features/text-mode/stores/question.store'
import { type EncounterState, useEncounterStore } from '@/stores/encounter'
import { type MrxState, useMrxStore } from '@/stores/mrx'
import { type TrackState, useTrackStore } from '@/stores/track'

interface Encounter {
  // engagementKey excluded: restoring a stale one makes syncEngagement reset the store
  // track.all excluded: the track list belongs to the boot config, not to an encounter
  mrx: Omit<MrxState, 'engagementKey'>,
  session: Omit<EncounterState, 'engagementKey'>,
  track: Omit<TrackState, 'engagementKey' | 'all'>,
  guidedMode: GuidedModeSnapshot,
  overallAssessment: OverallAssessmentJourney,
  textNote: NoteState,
  textQuestion: QuestionState,
  textEvidence: EvidenceState,
  textPatient: PatientState,
  textAlert: AlertStoreState,
  textDraft: DraftStoreSnapshot,
  textModeContext: TextModeAnalyticsContext | null,
  pausedRoute: string,
  pausedAt: string,
}

interface SavedEncountersState {
  saved: Encounter[]
}

export const useSavedEncountersStore = defineStore('savedEncounters', {
  persist: true,
  state: (): SavedEncountersState => ({
    saved: [],
  }),

  getters: {
    hasSaved(state): boolean {
      return state.saved.length > 0
    },
  },

  actions: {
    save(pausedRoute: string): void {
      const { engagementKey, ...session } = useEncounterStore().$state
      const { engagementKey: mrxEngagementKey, ...mrx } = useMrxStore().$state
      const { engagementKey: trackEngagementKey, all, ...track } = useTrackStore().$state
      const {
        clinicalContent, tracks, engagementKey: guidedModeEngagementKey,
        ...guidedModeProgress
      } = useGuidedModeStore().$state
      const draftState = useDraftStore().$state
      const encounter: Encounter = {
        mrx: structuredClone(mrx),
        session: structuredClone(session),
        track: structuredClone(track),
        guidedMode: structuredClone(guidedModeProgress),
        overallAssessment: structuredClone(useOverallAssessmentStore().$state),
        textNote: structuredClone(useNoteStore().$state),
        textQuestion: structuredClone(useQuestionStore().$state),
        textEvidence: structuredClone(useEvidenceStore().$state),
        textPatient: structuredClone(usePatientStore().$state),
        textAlert: structuredClone(useAlertStore().$state),
        textDraft: structuredClone({
          ...draftState,
          committed: { ...draftState.committed, incorrectIds: Array.from(draftState.committed.incorrectIds) },
        }),
        textModeContext: structuredClone(getActiveTextModeContext()),
        pausedRoute,
        pausedAt: new Date().toISOString(),
      }
      const existingIndex = this.saved.findIndex(
        (enc: Encounter) => enc.session.uuid === encounter.session.uuid
      )
      if (existingIndex !== -1) {
        this.saved[existingIndex] = encounter
      } else {
        this.saved.push(encounter)
      }
    },

    load(encounterId: string): string {
      const existing = this.saved.find((enc: Encounter) => enc.session.uuid === encounterId)
      if (!existing) {
        // Count only, and not awaited: the throw must not wait on an IndexedDB write.
        logger.error(
          LogCategory.ENCOUNTER,
          'encounter.resume.missing',
          { savedCount: this.saved.length },
          'No saved encounter matched the requested id',
        ).then()

        // No id: uncaught, so the message lands in the log store via unhandledrejection.
        throw new Error('No saved encounter for the requested id')
      }

      const pausedDurationMs = existing.session.pausedDurationMs
        + (Date.now() - new Date(existing.pausedAt).getTime())

      useMrxStore().resume(existing.mrx)
      useEncounterStore().resume({ ...existing.session, pausedDurationMs })
      useTrackStore().resume(existing.track)
      useGuidedModeStore().resume(existing.guidedMode)
      useOverallAssessmentStore().resume(existing.overallAssessment)
      useNoteStore().resume(existing.textNote)
      useQuestionStore().resume(existing.textQuestion)
      useEvidenceStore().resume(existing.textEvidence)
      usePatientStore().resume(existing.textPatient)
      useAlertStore().resume(existing.textAlert)
      useDraftStore().resume(existing.textDraft)

      if (existing.textModeContext) {
        setActiveTextModeContext(existing.textModeContext)
      } else {
        clearActiveTextModeContext()
      }

      return existing.pausedRoute
    },

    delete(encounterId: string): void {
      this.saved = this.saved.filter((enc: Encounter) => enc.session.uuid !== encounterId)
    },
  }
})
